import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";

// helper kecil
const groupTitle = (key) => {
  if (key === "stocks") return "Saham";
  if (key === "industries") return "Industri";
  if (key === "konglos") return "Konglo";
  if (key === "brokers") return "Broker";
  if (key === "chats") return "Chats";
  return key;
};

export default function GlobalSearchBox({
  className = "",
  placeholder = "Cari kode saham, perusahaan, chat, industri, konglo, broker...",
  autoFocus = false,
}) {
  const nav = useNavigate();
  const { token } = useAuth() || {};
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const boxRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const headers = useMemo(() => {
    const h = {};
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }, [token]);

  const groups = res?.groups || {};
  const hasAny =
    (groups?.stocks?.length || 0) +
      (groups?.industries?.length || 0) +
      (groups?.konglos?.length || 0) +
      (groups?.brokers?.length || 0) +
      (groups?.chats?.length || 0) >
    0;

  const goItem = (item) => {
    if (!item) return;

    // Tutup dropdown
    setOpen(false);

    // Routing by type
    if (item.type === "stock") {
      nav(`/stocks/${encodeURIComponent(item.kode_saham)}`);
      return;
    }

    // Industri: sector/industry/subindustry kita campur, tapi tetap ada type untuk filter
    if (item.type === "industry" || item.type === "sector" || item.type === "subindustry") {
      // contoh: lempar ke screener /stocks/all dengan query
      // sesuaikan route kamu:
      // - sector => ?sektor=...
      // - industry => ?industri=...
      // - subindustry => ?subindustri=...
      const key =
        item.type === "sector" ? "sektor" : item.type === "subindustry" ? "subindustri" : "industri";
      nav(`/stocks/all?${key}=${encodeURIComponent(item.label)}`);
      return;
    }

    if (item.type === "konglo") {
      nav(`/konglo/${encodeURIComponent(item.owner)}`); // sesuaikan route kamu
      return;
    }

    if (item.type === "broker") {
      nav(`/brokers/${encodeURIComponent(item.broker_code)}`); // sesuaikan route kamu
      return;
    }

    if (item.type === "chat") {
      localStorage.setItem("activeChatPublicId", String(item.id));
      nav(`/ask/c/${encodeURIComponent(it.public_id)}`);
      return;
    }
  };

  const goTopHit = () => {
    const top = res?.top_hit;
    if (top) return goItem(top);
    // fallback: kalau input terlihat ticker (<=6 & alnum) langsung coba buka
    const s = (q || "").trim().toUpperCase();
    if (s && /^[A-Z0-9]{2,10}$/.test(s)) {
      nav(`/stocks/${encodeURIComponent(s)}`);
      setOpen(false);
    }
  };

  const fetchSearch = async (text) => {
    const qq = (text || "").trim();
    setErr("");
    if (!qq) {
      setRes(null);
      setLoading(false);
      return;
    }

    // cancel sebelumnya
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const r = await fetch(`/api/search/global?q=${encodeURIComponent(qq)}&limit=8`, {
        headers,
        signal: ctrl.signal,
      });
      const js = await r.json();
      if (!r.ok) throw new Error(js?.detail || "Search failed");
      setRes(js);
      setOpen(true);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setErr(e?.message || "Error");
      setRes(null);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Debounce
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const qq = (q || "").trim();
    if (!qq) {
      setRes(null);
      setLoading(false);
      setErr("");
      return;
    }
    debounceRef.current = setTimeout(() => fetchSearch(qq), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, token]);

  // Click outside to close
  useEffect(() => {
    const onDown = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const renderItem = (item) => {
    // label yang ditampilkan per type
    if (item.type === "stock") {
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-100">{item.kode_saham}</div>
            {item.nama ? <div className="text-[12px] text-gray-400 truncate">{item.nama}</div> : null}
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">Saham</span>
        </div>
      );
    }

    if (item.type === "konglo") {
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-100 truncate">{item.owner}</div>
            <div className="text-[12px] text-gray-400">{item.count} saham</div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">Konglo</span>
        </div>
      );
    }

    if (item.type === "broker") {
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-100">{item.broker_code}</div>
            <div className="text-[12px] text-gray-400 truncate">{item.broker_name}</div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">Broker</span>
        </div>
      );
    }

    if (item.type === "chat") {
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-100 truncate">{item.title}</div>
            <div className="text-[12px] text-gray-400">Chat • Cluster {item.cluster_id}</div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">Chat</span>
        </div>
      );
    }

    // industries list campur: sector/industry/subindustry
    if (item.type === "industry" || item.type === "sector" || item.type === "subindustry") {
      const badge =
        item.type === "sector" ? "Sector" : item.type === "subindustry" ? "Subind" : "Industri";
      return (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-gray-100 truncate">{item.label}</div>
            <div className="text-[12px] text-gray-400">{item.count} saham</div>
          </div>
          <span className="text-[11px] px-2 py-0.5 rounded bg-gray-800 text-gray-300">{badge}</span>
        </div>
      );
    }

    return <div className="text-gray-200">Unknown</div>;
  };

  const groupedItems = useMemo(() => {
    // industries tetap 1 list campur (sector/industry/subindustry)
    return [
      ["stocks", groups?.stocks || []],
      ["industries", groups?.industries || []],
      ["konglos", groups?.konglos || []],
      ["brokers", groups?.brokers || []],
      ["chats", groups?.chats || []],
    ].filter(([, arr]) => (arr?.length || 0) > 0);
  }, [groups]);

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-xl border border-gray-700 bg-gray-900 px-3 py-2">
        <span className="text-gray-400">🔎</span>
        <input
          autoFocus={autoFocus}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") goTopHit();
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-gray-100 placeholder:text-gray-500"
        />

        {loading ? (
          <div className="text-[12px] text-gray-400">Loading...</div>
        ) : q ? (
          <button
            onClick={() => {
              setQ("");
              setRes(null);
              setErr("");
              setOpen(false);
            }}
            className="text-gray-400 hover:text-gray-200 text-[12px]"
            title="Clear"
          >
            ✕
          </button>
        ) : null}
      </div>

      {/* dropdown */}
      {open && (q.trim().length > 0) ? (
        <div className="absolute z-50 mt-2 w-full rounded-xl border border-gray-700 bg-gray-950 shadow-xl overflow-hidden">
          {err ? (
            <div className="p-3 text-sm text-red-300">{err}</div>
          ) : !loading && !hasAny ? (
            <div className="p-3 text-sm text-gray-400">Tidak ada hasil.</div>
          ) : (
            <div className="max-h-[70vh] overflow-auto">
              {/* top hit hint */}
              {res?.top_hit ? (
                <button
                  onClick={() => goItem(res.top_hit)}
                  className="w-full text-left px-3 py-2 border-b border-gray-800 hover:bg-gray-900"
                >
                  <div className="text-[11px] text-gray-400 mb-1">Top hit</div>
                  {renderItem(res.top_hit)}
                </button>
              ) : null}

              {/* grouped */}
              {groupedItems.map(([k, items]) => (
                <div key={k} className="border-b border-gray-900 last:border-b-0">
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500">
                    {groupTitle(k)}
                  </div>
                  <div className="px-1 pb-2">
                    {items.map((it, idx) => (
                      <button
                        key={`${k}-${idx}`}
                        onClick={() => goItem(it)}
                        className="w-full text-left px-2 py-2 rounded-lg hover:bg-gray-900"
                      >
                        {renderItem(it)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
